package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentChoiceResponse
import ai.jessie.shared.documents.DocumentDetailResponse

internal fun documentStatusLabel(status: String) =
    when (status) {
        "INBOX" -> "Inbox"
        "REVIEW" -> "In Prüfung"
        "ACCOUNTING" -> "Buchhaltung"
        "ARCHIVED" -> "Archiviert"
        "REJECTED" -> "Abgelehnt"
        else -> "Unbekannter Status"
    }

// A9 and A10: rejection and reactivation require a reason.
internal fun reasonRequired(from: String, target: String) =
    target == REJECTED || from == ARCHIVED || from == REJECTED

internal fun transitionLabel(from: String, target: String) =
    when {
        from == ARCHIVED || from == REJECTED -> "Reaktivieren"
        target == REVIEW -> "In Prüfung übernehmen"
        target == ACCOUNTING -> "An Buchhaltung übergeben"
        target == ARCHIVED -> "Archivieren"
        target == REJECTED -> "Ablehnen"
        else -> "Status ändern: ${documentStatusLabel(target)}"
    }

internal fun transitionConfirmLabel(from: String, target: String) =
    when {
        from == ARCHIVED || from == REJECTED -> "Reaktivierung bestätigen"
        target == REVIEW -> "Übernahme bestätigen"
        target == ACCOUNTING -> "Übergabe bestätigen"
        target == ARCHIVED -> "Archivierung bestätigen"
        target == REJECTED -> "Ablehnung bestätigen"
        else -> "Bestätigen"
    }

internal fun transitionConsequences(from: String, target: String) =
    when {
        from == ARCHIVED || from == REJECTED ->
            "Das Dokument kehrt in die Prüfung zurück und kann wieder bearbeitet werden. Der " +
                "Grund wird mit dir als Auslöser im Verlauf festgehalten."
        target == REVIEW -> "Das Dokument verlässt die Inbox und wird in Prüfung genommen."
        target == ACCOUNTING ->
            "Das Dokument geht an die Buchhaltung. Vor der Archivierung braucht es Dokumentdatum, " +
                "Währung, Netto- und Bruttobetrag."
        target == ARCHIVED && from == ACCOUNTING ->
            "Das Dokument wird archiviert und ist danach schreibgeschützt, bis es reaktiviert " +
                "wird. Die Aufbewahrungsfrist von 10 Jahren wird festgelegt."
        target == ARCHIVED ->
            "Das Dokument wird archiviert und ist danach schreibgeschützt, bis es reaktiviert " +
                "wird. Es durchläuft keine Buchhaltung."
        target == REJECTED ->
            "Das Dokument wird abgelehnt und ist danach schreibgeschützt, bis es reaktiviert " +
                "wird. Der Grund wird mit dir als Auslöser im Verlauf festgehalten."
        else -> "Bitte bestätige den Statuswechsel."
    }

// Wire names of the editable fields (B7) and the other recorded changes (B5).
internal fun documentFieldLabel(field: String) = FIELD_LABELS[field] ?: field

private val FIELD_LABELS =
    mapOf(
        "title" to "Titel",
        "documentType" to "Dokumentart",
        "summary" to "Zusammenfassung",
        "externalInvoiceId" to "Externe Rechnungsnummer",
        "issuerName" to "Aussteller",
        "recipientName" to "Empfänger",
        "documentDate" to "Dokumentdatum",
        "dueDate" to "Fälligkeitsdatum",
        "currency" to "Währung",
        "totalNet" to "Nettobetrag",
        "totalGross" to "Bruttobetrag",
        "categoryId" to "Kategorie",
        "folderId" to "Ordner",
        "accountingRelevant" to "Buchhaltungsrelevant",
        "status" to "Status",
        "reason" to "Grund",
        PAID_ON to "Zahlungsdatum",
        "paidAt" to "Zahlung erfasst",
        "retentionUntil" to "Aufbewahrung bis",
    )

// Step 12: a recorded value as the member knows it; IDs become the names still known here.
internal fun documentChangeValue(
    field: String,
    value: String?,
    detail: DocumentDetailResponse?,
): String =
    when {
        value == null -> "leer"
        field == "status" -> documentStatusLabel(value)
        field == "accountingRelevant" -> if (value == "true") "Ja" else "Nein"
        field == "categoryId" -> choiceName(value, detail?.categories, detail?.category)
        field == "folderId" -> choiceName(value, detail?.folders, detail?.folder)
        else -> value
    }

private fun choiceName(
    id: String,
    choices: List<DocumentChoiceResponse>?,
    current: DocumentChoiceResponse?,
): String =
    (choices.orEmpty() + listOfNotNull(current))
        .firstOrNull { it.id.equals(id, ignoreCase = true) }
        ?.name ?: "nicht mehr verfügbar"

internal fun documentEventLabel(eventType: String) =
    when (eventType) {
        "document.created.v1" -> "Hochgeladen"
        "document.details_changed.v1" -> "Angaben geändert"
        "document.status_changed.v1" -> "Status geändert"
        "document.rejected.v1" -> "Abgelehnt"
        "document.reactivated.v1" -> "Reaktiviert"
        "document.payment_recorded.v1" -> "Zahlung erfasst"
        "document.payment_corrected.v1" -> "Zahlung korrigiert"
        "document.payment_cleared.v1" -> "Zahlung entfernt"
        "document.removal_cancelled.v1" -> "Löschen abgebrochen"
        "document.removal_denied.v1" -> "Löschen nicht zulässig"
        else -> "Änderung"
    }

// The server's field problem reasons as one correction each; dates name the permitted range (A6).
internal fun documentFieldCorrection(
    field: String,
    reason: String,
    detail: DocumentDetailResponse?,
): String =
    when (reason) {
        "REQUIRED" ->
            if (field == "reason") "Bitte gib einen Grund an (höchstens $MAX_REASON Zeichen)."
            else "${documentFieldLabel(field)} ist erforderlich."
        "TOO_LONG" -> "${documentFieldLabel(field)} ist zu lang."
        "TOO_LARGE" -> "Der Betrag muss kleiner als 100’000’000 sein."
        "BELOW_NET" -> "Der Bruttobetrag darf nicht kleiner als der Nettobetrag sein."
        "UNAVAILABLE" ->
            "Diese Auswahl ist nicht verfügbar. Bitte wähle eine aktive ${documentFieldLabel(field)}."
        "NOT_EDITABLE" -> "${documentFieldLabel(field)} kannst du nicht ändern."
        "AFTER_PAYMENT" -> "Das Dokumentdatum darf nicht nach dem Zahlungsdatum liegen."
        "BEFORE_DOCUMENT_DATE" ->
            "Das Zahlungsdatum darf nicht vor dem Dokumentdatum " +
                "(${detail?.details?.documentDate.orEmpty()}) liegen."
        "AFTER_TODAY" ->
            "Das Zahlungsdatum darf nicht nach heute (${detail?.today.orEmpty()}) liegen."
        "BEFORE_INVOICE_DATE" ->
            "Das Zahlungsdatum darf nicht vor dem Datum der verknüpften Rechnung liegen."
        else -> invalidCorrection(field)
    }

private fun invalidCorrection(field: String) =
    when (field) {
        "documentDate",
        "dueDate",
        PAID_ON -> "Gib ein gültiges Datum ein."
        "totalNet",
        "totalGross" -> "Gib einen Betrag mit höchstens zwei Nachkommastellen ein, z. B. 120.50."
        "currency" -> "Gib einen ISO-4217-Währungscode in Grossbuchstaben ein, z. B. CHF."
        else -> "${documentFieldLabel(field)} ist ungültig."
    }

internal const val INBOX = "INBOX"
internal const val REVIEW = "REVIEW"
internal const val ACCOUNTING = "ACCOUNTING"
internal const val ARCHIVED = "ARCHIVED"
internal const val REJECTED = "REJECTED"
internal const val PAID_ON = "paidOn"
internal const val MAX_REASON = 1000
